import { useEffect } from 'react';
import './FireFangAnimation.css';

export default function FireFangAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const battleBg = playerEl.closest('.battle-bg') || document.querySelector('.battle-bg');
    if (!battleBg) {
      console.error('Battle background not found');
      onComplete();
      return;
    }

    const pRect = playerEl.getBoundingClientRect();
    const oRect = opponentEl.getBoundingClientRect();
    const bRect = battleBg.getBoundingClientRect();

    const startX = (pRect.left + pRect.width / 2) - bRect.left;
    const startY = (pRect.top + pRect.height / 2) - bRect.top;
    const endX = (oRect.left + oRect.width / 2) - bRect.left;
    const endY = (oRect.top + oRect.height / 2) - bRect.top;

    const elements = [];
    const timers = [];

    // Player subtle fiery charge
    playerEl.classList.add('firefang-charge');

    // Muzzle flare at launch
    const muzzle = document.createElement('div');
    muzzle.className = 'firefang-muzzle';
    muzzle.style.left = `${startX}px`;
    muzzle.style.top = `${startY}px`;
    battleBg.appendChild(muzzle);
    elements.push(muzzle);
    const muzzleRm = setTimeout(() => muzzle.remove(), 240);
    timers.push(muzzleRm);

    // Comet streaks along path
    const angleRad = Math.atan2(endY - startY, endX - startX);
    for (let i = 0; i < 3; i++) {
      const st = document.createElement('div');
      st.className = 'firefang-streak';
      st.style.left = `${startX}px`;
      st.style.top = `${startY}px`;
      const jitter = (Math.random() - 0.5) * 8; // degrees
      st.style.setProperty('--rot', `${(angleRad * 180 / Math.PI) + jitter}deg`);
      st.style.setProperty('--dx', `${endX - startX}px`);
      st.style.setProperty('--dy', `${endY - startY}px`);
      battleBg.appendChild(st);
      elements.push(st);
      const stRm = setTimeout(() => st.remove(), 360);
      timers.push(stRm);
    }

    // Fireball dash from player to opponent to sell the "fang lunge"
    const fireball = document.createElement('div');
    fireball.className = 'firefang-fireball';
    fireball.style.left = `${startX}px`;
    fireball.style.top = `${startY}px`;
    fireball.style.setProperty('--dx', `${endX - startX}px`);
    fireball.style.setProperty('--dy', `${endY - startY}px`);
    battleBg.appendChild(fireball);
    elements.push(fireball);

    const spawnImpact = () => {
      // Opponent hit reaction
      opponentEl.classList.add('firefang-hit');
      const hitRm = setTimeout(() => opponentEl.classList.remove('firefang-hit'), 320);
      timers.push(hitRm);

      // Camera shake
      battleBg.classList.add('firefang-shake');
      const shakeRm = setTimeout(() => battleBg.classList.remove('firefang-shake'), 260);
      timers.push(shakeRm);

      // Core flash at bite center
      const core = document.createElement('div');
      core.className = 'firefang-core';
      core.style.left = `${endX}px`;
      core.style.top = `${endY}px`;
      battleBg.appendChild(core);
      elements.push(core);
      const coreRm = setTimeout(() => core.remove(), 220);
      timers.push(coreRm);

      // Impact ring
      const ring = document.createElement('div');
      ring.className = 'firefang-impact-ring';
      ring.style.left = `${endX}px`;
      ring.style.top = `${endY}px`;
      battleBg.appendChild(ring);
      elements.push(ring);
      const ringRm = setTimeout(() => ring.remove(), 520);
      timers.push(ringRm);

      // Fire sparks
      for (let i = 0; i < 18; i++) {
        const sp = document.createElement('div');
        sp.className = 'firefang-spark';
        sp.style.left = `${endX}px`;
        sp.style.top = `${endY}px`;
        const angle = Math.random() * Math.PI * 2;
        const dist = 26 + Math.random() * 34;
        sp.style.setProperty('--dx', `${Math.cos(angle) * dist}px`);
        sp.style.setProperty('--dy', `${Math.sin(angle) * dist}px`);
        battleBg.appendChild(sp);
        elements.push(sp);
        const spRm = setTimeout(() => sp.remove(), 560);
        timers.push(spRm);
      }

      // Scorch mark
      const scorch = document.createElement('div');
      scorch.className = 'firefang-scorch';
      scorch.style.left = `${endX}px`;
      scorch.style.top = `${endY + 8}px`;
      battleBg.appendChild(scorch);
      elements.push(scorch);
      const scorchRm = setTimeout(() => scorch.remove(), 1000);
      timers.push(scorchRm);

      // Rising smoke plumes
      for (let i = 0; i < 3; i++) {
        const sm = document.createElement('div');
        sm.className = 'firefang-smoke';
        sm.style.left = `${endX}px`;
        sm.style.top = `${endY - 4}px`;
        sm.style.setProperty('--sx', `${(Math.random() - 0.5) * 20}px`);
        battleBg.appendChild(sm);
        elements.push(sm);
        const smRm = setTimeout(() => sm.remove(), 950);
        timers.push(smRm);
      }

      // Complete shortly after impact
      const done = setTimeout(() => {
        timers.forEach(clearTimeout);
        elements.forEach(el => el.isConnected && el.remove());
        playerEl.classList.remove('firefang-charge');
        opponentEl.classList.remove('firefang-hit');
        onComplete();
      }, 700);
      timers.push(done);
    };

    const spawnFangs = () => {
      // Two fangs closing on target
      const leftFang = document.createElement('div');
      leftFang.className = 'firefang-fang firefang-fang--left';
      leftFang.style.left = `${endX}px`;
      leftFang.style.top = `${endY}px`;

      const rightFang = document.createElement('div');
      rightFang.className = 'firefang-fang firefang-fang--right';
      rightFang.style.left = `${endX}px`;
      rightFang.style.top = `${endY}px`;

      battleBg.appendChild(leftFang);
      battleBg.appendChild(rightFang);
      elements.push(leftFang, rightFang);

      // Trigger impact slightly after the bite closes
      const impactTimer = setTimeout(spawnImpact, 220);
      timers.push(impactTimer);

      // Clean up fangs after animation
      const fangRm = setTimeout(() => {
        leftFang.remove();
        rightFang.remove();
      }, 400);
      timers.push(fangRm);
    };

    const handleFireballEnd = () => {
      fireball.removeEventListener('animationend', handleFireballEnd);
      if (fireball.isConnected) fireball.remove();
      spawnFangs();
    };

    fireball.addEventListener('animationend', handleFireballEnd);

    // Safety cleanup
    const cleanupTimer = setTimeout(() => {
      fireball.removeEventListener('animationend', handleFireballEnd);
      elements.forEach(el => el.isConnected && el.remove());
      playerEl.classList.remove('firefang-charge');
      opponentEl.classList.remove('firefang-hit');
      onComplete();
    }, 1800);
    timers.push(cleanupTimer);

    return () => {
      fireball.removeEventListener('animationend', handleFireballEnd);
      timers.forEach(clearTimeout);
      elements.forEach(el => el.isConnected && el.remove());
      playerEl.classList.remove('firefang-charge');
      opponentEl.classList.remove('firefang-hit');
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

FireFangAnimation.moveType = 'fire';
